import * as React from "react";
import {
    createContext,
    Dispatch,
    FC,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState
} from "react";
import { useMapContext } from "./MapContext";
import { Layer } from "ol/layer";
import { config, getLayerPositions, ILayer, ILayerGroup, ILayerImage, ILayerState } from "../Map/config";
import { getMap } from "../Map/GeoLiteMap";
import { Source } from "ol/source";
import LayerRenderer from "ol/renderer/Layer";
import { checkScale } from "../Map/Toc/helpers";
import axios from "axios";
import { GroupVisibility, LayerAction, LayerType } from "../Map/enums";
import { getUrlParam } from "../Map/utils";
import { useAppContext } from "./AppContext";
import { getDataLayers } from "../Map/layerFunctions";

export interface ILayersContext {
    mapLayers: Layer[];
    setMapLayers: Dispatch<SetStateAction<Layer[]>>;
    legends: ILegend[];
    setLegends: Dispatch<SetStateAction<ILegend[]>>;
    // storeLayerSettings: (layerStates: ILayerState[]) => void;
    onToggle: (path: string, type: string) => void;
    setGroupVisibility: (layerId: string, state: GroupVisibility) => void;
    showLayerMetadata: (
        e: React.MouseEvent<HTMLSpanElement, MouseEvent>,
        layerConfig: ILayer | undefined,
        layer: ILayerState
    ) => void;
}

interface ILegend {
    name: string;
    jsonData: { layers: ILegendLayer[] };
}

interface ILayerSettings {
    expanded: boolean;
    visible: boolean;
    shown?: boolean;
}

interface ILegendImage {
    label: string;
    url: string;
    imageData: string;
    contentType: string;
    height: number;
    width: number;
}

interface ILegendLayer {
    layerId: number;
    layerName: string;
    layerType: string;
    minScale: number;
    maxScale: number;
    legend: ILegendImage[];
}

const appid = encodeURI(window.location.host + window.location.pathname);
const storageName = "GeoLiteLayers-" + appid; // storageName = props.isMainMenu ? "layerSettings" : "secondaryLayerSettings";;
let showedMetaDataPopupIds: string[] = [];

const LayersContext = createContext<ILayersContext>(null);

export const useLayersContext = (): ILayersContext => useContext(LayersContext);

export const LayersContextProvider: FC = (props: React.PropsWithChildren<ILayersContext>) => {
    const { activeToken } = useAppContext();
    const { layerStates, setLayerStates, configA } = useMapContext();

    const [mapLayers, setMapLayers] = useState<Layer[]>([]);
    const [legends, setLegends] = useState<ILegend[]>();
    const [currentToken, setCurrentToken] = useState<string>();

    const layerStatesRef = useRef<ILayerState[]>(layerStates);
    const mapLayersRef = useRef<Layer[]>(mapLayers);
    const layersShownRef = useRef<boolean>(false);

    const displayLayerIdsLayer = configA.layers.map((l) => l.id);
    const displayLayerGroupIds = configA.layerGroups ? configA.layerGroups.map((lg) => lg.id) : [];
    const displayLayerIds = [...displayLayerIdsLayer, ...displayLayerGroupIds];

    useEffect(() => {
        getMap() && setMapLayers(getMap().getLayers().getArray() as Layer<Source, LayerRenderer<any>>[]);
        document.addEventListener("moveend", onMoveEnd);
    }, []);

    useEffect(() => {
        layerStatesRef.current = layerStates;
    }, [layerStates]);

    useEffect(() => {
        mapLayersRef.current = mapLayers;
        mapLayers && getLayerLegendJson();
    }, [mapLayers]);

    useEffect(() => {
        legends && getRootLayers();
    }, [legends]);

    useEffect(() => {
        if (activeToken && !currentToken) {
            const authLayers = getDataLayers(configA, true);
            const currentLayers = getMap().getLayers().getArray();
            const notDuplicates = authLayers.filter((al) => {
                const id = al.get("id");
                return !currentLayers.find((cl) => cl.get("id") === id);
            });
            if (notDuplicates.length) {
                const newLayers = [...currentLayers, ...notDuplicates] as Layer<Source, LayerRenderer<any>>[];
                getMap().setLayers(newLayers);
                setMapLayers(newLayers);
            }
            setCurrentToken(activeToken);
        }
    }, [activeToken, currentToken]);

    const getRootLayers = useCallback(() => {
        if (!mapLayers || !legends) {
            return;
        }

        const settings = getSettings();

        const esriLayers = mapLayers
            .filter((layer) =>
                displayLayerIds && !displayLayerIds.includes(layer.get("id"))
                    ? false
                    : layer.get("id") !== config.default.highLightLayerId &&
                      layer.get("type") !== "graphics" &&
                      !isTocLayer(layer)
            )
            .filter((layer) => !configA.basemaps.find((basemap) => basemap.id === layer.get("id")))
            .reverse();

        const layerGroups = getLayerGroups(configA);
        const layersIdsFromGroups: string[] = layerGroups.reduce((layers, group) => [...group.layers, ...layers], []);

        const esriLayerStates = esriLayers
            .filter((layer) => !layersIdsFromGroups.includes(layer.get("id")))
            .map((layer) => esriLayerToLayerState(layer, settings));

        const groupLayerStates = layerGroups
            .filter((layer: ILayerState) => (displayLayerIds ? displayLayerIds.includes(layer.id) : true))
            .map((layer) => groupLayerToLayerState(layer, settings));

        const layerStates = esriLayerStates.concat(groupLayerStates);
        const positions = getLayerPositions();

        layerStates.map((state: ILayerState) =>
            positions.find((id, index) => {
                if (state.layerId === id) {
                    state.position = index;
                }
            })
        );

        const newLayerStates = layerStates.sort((a, b) => Number(a.position) - Number(b.position));
        setLayerStates(newLayerStates);
        hideHiddenLayerGroupLayers(newLayerStates);
        storeLayerSettings(newLayerStates);
        getMap().once("rendercomplete", () => {
            if (!layersShownRef.current) {
                layersShownRef.current = true;
                setTimeout(() => {
                    // @ts-ignore
                    const scale = document.mapScale;
                    showLayers(layerStatesRef.current, scale);
                }, 500);
            }
        });
    }, [mapLayers, legends]);

    const isTocLayer = (layer: Layer | ILayerState) =>
        configA.layers.some(
            (element): boolean =>
                (layer as Layer).get("id").replace(/_/g, "") === element.id.replace(/_/g, "") && element.hideInToc
        );

    const onMoveEnd = (e) => layerStatesRef.current && checkAllLayerScale(layerStatesRef.current, e.detail);

    const showLayers = (layers: ILayerState[], scale: number) => {
        layers.forEach((layer) => {
            const allowedToShow = checkScale(scale, layer.minScale, layer.maxScale);
            toggleLayerVisibility(layer.id, allowedToShow && layer.shown && layer.visible);
            if (
                layer.type === "LayerGroup" &&
                allowedToShow &&
                layer.shown &&
                layer.visible &&
                layer.sublayers &&
                layer.sublayers.length
            ) {
                layer.sublayers.map((sl) => toggleLayerVisibility(sl.id, sl.visible));
            }
        });
    };

    const checkAllLayerScale = (layers: ILayerState[], scale: number) =>
        layers.forEach((layer) => {
            const allowedToShow = checkScale(scale, layer.minScale, layer.maxScale);
            layer.shown !== allowedToShow && onToggle(layer.id, LayerAction.Shown);
            layer.sublayers && checkAllLayerScale(layer.sublayers, scale);
        });

    const getLayerLegendJson = useCallback(async () => {
        const legendsNew = await Promise.all(
            //@ts-ignore
            mapLayers.map(async (layer) => {
                const id = layer.get("id");
                const url = layer.get("url");
                const hidden =
                    layer.getZIndex() === 0 || // Do not request legend for basemap layers
                    configA.layers.find((conf) => conf.id === id && (conf.hideInToc || conf.hideLegend));
                try {
                    if (layer && url && !hidden) {
                        const response = await axios(url + "/legend?f=json");
                        return { name: id, jsonData: response.data } as ILegend;
                    }
                } catch (error) {
                    console.error(error);
                    return;
                }
            })
        );

        legendsNew && setLegends(legendsNew.filter((l) => l));
    }, [mapLayers]);

    const esriLayerToLayerState = (layer: Layer, settings: string | null): ILayerState => {
        const id = layer.get("id");
        const layerType = layer.get("type");
        const layerId = "_" + id + "_";
        const visibleLayers = layer.get("visibleLayers");
        const visible =
            settings && settings[layerId] ? settings[layerId].visible : layer.get("defaultVisible") || !!visibleLayers;
        const layerConfig = configA.layers.find((config) => config.id === id);
        const defaultExpanded = (layerConfig && layerConfig.expanded) || false;
        const sublayers =
            (layerType === "MapImageLayer" || layerType === "TileLayer") && layer.get("layers")
                ? layer
                      .get("layers")
                      .filter((sl: ILayerState) => (visibleLayers?.length ? visibleLayers.includes(sl.id) : true))
                      .map((l: Layer) =>
                          getSubLayerState(
                              id,
                              id,
                              l,
                              settings,
                              layerType,
                              defaultExpanded,
                              layer.get("type"),
                              visibleLayers
                          )
                      )
                : undefined;

        return {
            type: layerType,
            typeName: layerType,
            rootLayerId: id,
            layerId: id,
            id: layerId,
            title: layer.get("title"),
            expanded: defaultExpanded || (settings && settings[layerId] ? settings[layerId].expanded : false),
            visible,
            first: true,
            error: layer.get("loadError") ? layer.get("loadError").message : undefined,
            //@ts-ignore
            shown: checkScale(document.mapScale, layer.get("minScale"), layer.get("maxScale")),
            minScale: layer.get("minScale"),
            maxScale: layer.get("maxScale"),
            hideLegend: settings && settings[layerId] ? settings[layerId].hideLegend : false,
            layers: [],
            sublayers,
            hideVisibility: layerType === LayerType.LayerGroup ? true : false,
            showInMapDownloadWidget: layer.get("showInMapDownloadWidget")
        };
    };

    const groupLayerToLayerState = (layer: any, settings: string | null) => {
        const id = layer.id;
        const layerType = layer.type;
        const layerId = "_" + id + "_";
        const visible = settings && settings[layerId] ? settings[layerId].visible : layer.visible;
        const layerConfig = configA.layerGroups.find((config) => config.id === id);
        const defaultExpanded = (layerConfig && layerConfig.expanded) || false;
        const sublayers = layer.layers
            ? layer.layers.map((l) => {
                  const mapL = mapLayers.find((ml) => ml.get("id") === l);
                  return getGroupSubLayerState(id, id, mapL, settings, mapL.get("type"), false);
              })
            : undefined;

        return {
            type: layerType,
            typeName: layerType,
            rootLayerId: id,
            layerId: id,
            id: layerId,
            title: layer.title,
            expanded: defaultExpanded || (settings && settings[layerId] ? settings[layerId].expanded : false),
            visible,
            first: true,
            error: (layer as any).loadError ? (layer as any).loadError.message : undefined,
            //@ts-ignore
            shown: checkScale(document.mapScale, layer.minScale, layer.maxScale),
            minScale: layer.minScale,
            maxScale: layer.maxScale,
            hideLegend: settings && settings[layerId] ? settings[layerId].hideLegend : false,
            layers: layer.layers,
            sublayers,
            hideVisibility: false,
            showInMapDownloadWidget: layer.showInMapDownloadWidget
        };
    };

    const getLegend = (name: string, id: number, legends: ILegend[]) => {
        const result = legends.find((legend) => legend.name === name);
        if (!result) {
            return;
        }
        const image = result.jsonData.layers.find((legendLayer: ILegendLayer) => legendLayer.layerId === id);

        return image ? image.legend : null;
    };

    const getGroupSubLayerState = useCallback(
        (
            rootLayerId: string | null,
            parentLayerId: string,
            layerObject,
            settings: string | null,
            type: string,
            defaultExpanded: boolean
        ): ILayerState => {
            const layer = layerObject;
            const layerId = layer.get("id");
            const id = "_" + parentLayerId + "_" + layerId + "_";
            const visibleLayers = layer.get("visibleLayers");
            const visible =
                settings && settings[id] ? settings[id].visible : !!visibleLayers || layer.get("defaultVisible");
            const sublayers =
                type !== "WMS" && layer.get("layers")
                    ? layer
                          .get("layers")
                          .filter((sl: ILayerState) => (visibleLayers?.length ? visibleLayers.includes(sl.id) : true))
                          .map((sublayer) =>
                              getSubLayerState(
                                  rootLayerId,
                                  id,
                                  sublayer,
                                  settings,
                                  type,
                                  defaultExpanded,
                                  LayerType.LayerGroup,
                                  visibleLayers
                              )
                          )
                          .reverse()
                    : undefined;

            return {
                type,
                typeName: layer.get("type"),
                rootLayerId,
                layerId,
                id,
                expanded: defaultExpanded || (settings && settings[id] ? settings[id].expanded : false),
                visible,
                title: layer.get("title"),
                first: false,
                // image: images,
                //@ts-ignore
                shown: checkScale(document.mapScale, layer.get("minScale"), layer.get("maxScale")),
                minScale: layer.get("minScale"),
                maxScale: layer.get("maxScale"),
                hideVisibility: false,
                hideLegend: settings && settings[id] ? settings[id].hideLegend : false,
                layers: sublayers,
                sublayers
            };
        },
        [mapLayers, legends]
    );

    const getSubLayerState = useCallback(
        (
            rootLayerId: string | null,
            parentLayerId: string,
            layerObject,
            settings: string | null,
            type: string,
            defaultExpanded: boolean,
            parentLayerType?: string,
            visibleLayers?: any[]
        ): ILayerState => {
            const layer = layerObject;
            const id = "_" + parentLayerId + "_" + layer.id + "_";
            const visible = settings && settings[id] ? settings[id].visible : true; // layer.visible;
            const layerName = parentLayerId.split("_").filter((path) => !!path)[
                parentLayerType === LayerType.LayerGroup ? 1 : 0
            ];
            const legend = getLegend(layerName, layer.id, legends);
            const images: ILayerImage[] = legend
                ? legend.map((element: ILegendImage) => {
                      const image = "data:" + element.contentType + ";base64," + element.imageData;
                      return { image, label: element.label };
                  })
                : [];
            const sublayers =
                type !== "WMS" && layer.sublayers
                    ? layer.sublayers
                          .filter((sl: ILayerState) => (visibleLayers?.length ? visibleLayers.includes(sl.id) : true))
                          .map((sublayer) =>
                              getSubLayerState(
                                  rootLayerId,
                                  id,
                                  sublayer,
                                  settings,
                                  type,
                                  defaultExpanded,
                                  parentLayerType,
                                  visibleLayers
                              )
                          )
                          .reverse()
                    : undefined;

            return {
                type,
                typeName: layer.type,
                rootLayerId,
                layerId: layer.id,
                id,
                expanded: defaultExpanded || (settings && settings[id] ? settings[id].expanded : false),
                visible,
                title: layer.name,
                first: false,
                image: images,
                //@ts-ignore
                shown: checkScale(document.mapScale, layer.minScale, layer.maxScale),
                minScale: layer.minScale,
                maxScale: layer.maxScale,
                hideVisibility: type === LayerType.TileLayer ? true : false,
                hideLegend: settings && settings[id] ? settings[id].hideLegend : false,
                layers: (layer as unknown as ILayerState).layers,
                sublayers
            };
        },
        [mapLayers, legends]
    );

    const findSublayer = (layers: ILayerState[], path: string[], iteration: number, view: boolean) => {
        let iter = iteration;
        layers.some((layer: ILayerState) => {
            // TODO: check for null pointer?
            // if (layer.id && layer.id.toString() === path[iter]) {
            if (layer.layerId.toString() === path[iter]) {
                if (iter === path.length - 1) {
                    layer.visible = view;
                    return true;
                }
                if (layer.sublayers && !(layer.type && layer.type === LayerType.TileLayer)) {
                    iter++;
                    findSublayer(layer.sublayers as ILayerState[], path, iter, view);
                }
            }
            return false;
        });
    };

    const toggleVisibilityRecursive = (layers: Layer[], path: string[], iteration: number, view: boolean) => {
        let iter = iteration;
        const rootLayer = layerStatesRef.current.find((layer) => layer.layerId === path[iter]);
        if (rootLayer && rootLayer.type === LayerType.LayerGroup) {
            iter++;
        }
        layers.some((layer) => {
            // TODO: check for null pointer?
            // if (layer.id && layer.id.toString() === path[iter]) {
            //@ts-ignore
            if (layer.get && layer.get("id").toString() === path[iter]) {
                if (iter === path.length - 1) {
                    //@ts-ignore
                    const allowedToShow = checkScale(document.mapScale, layer.get("minScale"), layer.get("maxScale"));
                    layer.setVisible(view && allowedToShow);
                    return true;
                }
                if (layer.get("layers")?.length && !(layer.get("type") === LayerType.TileLayer)) {
                    iter++;
                    toggleVisibilityRecursive(layer.get("layers"), path, iter, view);
                }
            }
            return false;
        });
    };

    const toggleLayerVisibility = useCallback(
        (id: string, view: boolean) => {
            const explodedPath = id.split("_");
            const pathArray = explodedPath.filter((path) => !!path);
            toggleVisibilityRecursive(mapLayersRef.current as Layer[], pathArray, 0, view);
        },
        [mapLayers]
    );

    const updateLayerStates = (newLayerStates: ILayerState[], path: string, type: string, stateValue?: boolean) => {
        newLayerStates.forEach((layer) => {
            if (!path.includes(layer.id)) {
                return;
            }
            if (layer.id.toString() === path) {
                if (type === LayerAction.View) {
                    layer.visible = stateValue !== undefined ? stateValue : !layer.visible;
                    layer.shown && toggleLayerVisibility(layer.id, layer.visible);
                    layer.visible && layerStatesRef.current && enableViewOnAll(layerStatesRef.current, path);
                } else if (type === LayerAction.Expand) {
                    layer.expanded = !layer.expanded;
                } else if (type === LayerAction.Shown) {
                    layer.shown = stateValue !== undefined ? stateValue : !layer.shown;
                    layer.visible && toggleLayerVisibility(layer.id, layer.shown);
                }
            } else if (layer.sublayers) {
                updateLayerStates(layer.sublayers, path, type, stateValue);
            }
        });
        return newLayerStates;
    };

    const enableViewOnAll = (layers: ILayerState[], path: string) => {
        layers.forEach((layer) => {
            if (path.includes(layer.id)) {
                if (layer.id.toString() === path) {
                    return;
                }
                layer.visible = true;
                toggleLayerVisibility(layer.id, true);
                layer.sublayers && enableViewOnAll(layer.sublayers, path);
            }
        });
    };

    const gatherSettings = (settings: ILayerSettings, layers: ILayerState[]) => {
        layers.forEach((layer) => {
            settings[layer.id] = { expanded: layer.expanded, visible: layer.visible };
            layer.sublayers && gatherSettings(settings, layer.sublayers);
        });
        return settings;
    };

    const storeLayerSettings = (newLayerStates) => {
        if (!newLayerStates) {
            return;
        }
        let settings = {} as ILayerSettings;
        settings = gatherSettings(settings, newLayerStates);
        localStorage.setItem(storageName, JSON.stringify(settings));
    };

    const onToggle = useCallback(
        (path: string, type: string, stateValue?: boolean) => {
            if (!layerStatesRef.current || !layerStatesRef.current.length) {
                return;
            }
            let newLayerStates = [...layerStatesRef.current];
            type === LayerAction.Expand && !configA.layersMultiExpand && closeExpandedItem(newLayerStates, path);

            newLayerStates = updateLayerStates(newLayerStates, path, type, stateValue);
            setLayerStates(newLayerStates);
            storeLayerSettings(newLayerStates);

            const layerGroup = newLayerStates.find(
                (stateLayer) => stateLayer.id === path && stateLayer.type === LayerType.LayerGroup
            );

            if (layerGroup && type === LayerAction.View) {
                layerGroup.visible
                    ? setVisibilityFromState(mapLayers, layerGroup, true)
                    : setVisibilityToFalse(mapLayers, layerGroup, true);
            }
        },
        [mapLayers]
    );

    const closeExpandedItem = (newLayerStates: ILayerState[], path: string) => {
        for (const element of newLayerStates) {
            !path.includes(element.id) && element.expanded && (element.expanded = false);
        }
    };

    const setVisibilityToFalse = (mapLayers: Layer[], layer: ILayerState, recurse: boolean) => {
        if (!layer.sublayers) {
            return;
        }
        for (const subLayer of layer.sublayers) {
            const mapLayer = mapLayers.find((mapLayer) => mapLayer.get("id") === subLayer.layerId);
            mapLayer && mapLayer.setVisible(false);
            recurse && setVisibilityToFalse(mapLayers, subLayer, recurse);
        }
    };

    const setVisibilityFromState = (mapLayers: Layer[], layer: ILayerState, recurse: boolean) => {
        if (!layer.sublayers) {
            return;
        }
        for (const subLayer of layer.sublayers) {
            const mapLayer = mapLayers.find((mapLayer) => mapLayer.get("id") === subLayer.layerId);
            //@ts-ignore
            const allowedToShow = checkScale(document.mapScale, subLayer.minScale, subLayer.maxScale);
            mapLayer && mapLayer.setVisible(subLayer.visible && allowedToShow);
            recurse && setVisibilityFromState(mapLayers, subLayer, recurse);
        }
    };

    const setSublayerVisibility = (layer: ILayerState, visible: boolean, recurse: boolean) => {
        if (!layer.sublayers) {
            return;
        }
        for (const subLayer of layer.sublayers) {
            subLayer.visible = visible;
            toggleLayerVisibility(subLayer.id, visible);
            recurse && setSublayerVisibility(subLayer, visible, recurse);
        }
    };

    const setGroupVisibility = (layerId: string, state: GroupVisibility) => {
        if (!layerStatesRef.current) {
            return;
        }

        const layerStatesMod = [...layerStatesRef.current];

        const newlayerState = layerStatesMod.find((state) => state.id === layerId);
        if (!newlayerState) {
            return;
        }

        const visible = state === GroupVisibility.VISIBLE;
        newlayerState.visible = visible;
        toggleLayerVisibility(newlayerState.id, visible);
        setSublayerVisibility(newlayerState, visible, true);
        setLayerStates(layerStatesMod);
        storeLayerSettings(layerStatesMod);
    };

    const hideHiddenLayerGroupLayers = useCallback(
        (layers: ILayerState[]) => {
            for (const layer of layers) {
                if (
                    !(
                        layer.type === LayerType.LayerGroup &&
                        !layer.visible &&
                        layer.sublayers &&
                        layer.sublayers.length
                    )
                ) {
                    continue;
                }
                for (const sublayer of layer.sublayers) {
                    if (!sublayer.visible) {
                        continue;
                    }
                    mapLayers.find(
                        (mapLayer: Layer) => mapLayer.get("id") === sublayer.layerId && mapLayer.setVisible(false)
                    );
                }
            }
        },
        [mapLayers]
    );

    const showLayerMetadata = (
        e: React.MouseEvent<HTMLSpanElement, MouseEvent>,
        layerConfig: ILayer | undefined,
        layer: ILayerState
    ) => {
        e.stopPropagation();
        // const popup = this.props.popup ? this.props.popup.current : null;
        // if (!layerConfig || !popup) {
        //     return;
        // }

        // this.showedMetaDataPopupIds.forEach((popupId) => popup.closePopup(popupId));
        showedMetaDataPopupIds = [];

        const subLayerUrlPart = layer.layerId === layerConfig.id ? "/info/" : `/${layer.layerId}/`;
        // TODO: not sure about the sub layers, some sublayer metdata returns not found e.g.
        // https://lvmgeopublic.lvm.lv:44443/arcgis/rest/services/MApsP/s_MAPtourism/MapServer/1/metadata/info?format=fgdc&output=html
        axios.get(layerConfig.url + subLayerUrlPart + "metadata?format=fgdc&output=html").then((response) => {
            const domParser = new DOMParser();
            const document = domParser.parseFromString(response.data, "text/html");
            const xmlSerializer = new XMLSerializer();
            const body = document.getElementsByTagName("body")[0];
            // this.showedMetaDataPopupIds.push(popup.showPopup(xmlSerializer.serializeToString(body)));
        });
    };

    const values = {
        mapLayers,
        setMapLayers,
        legends,
        setLegends,
        // storeLayerSettings,
        onToggle,
        setGroupVisibility,
        showLayerMetadata
    };

    return <LayersContext.Provider value={values}>{props.children}</LayersContext.Provider>;
};

const hasCustomConfig = () => !!getUrlParam("config");

const getSettings = () => {
    const layerSettings = localStorage.getItem(storageName);
    let storedSettings;
    try {
        storedSettings = layerSettings ? JSON.parse(layerSettings) : null;
    } catch (e) {
        storedSettings = null;
    }

    return hasCustomConfig() ? null : storedSettings;
};

const getLayerGroups = (config) => {
    if (!config.layerGroups) {
        return [];
    }
    return config.layerGroups.map((layerGroup: ILayerGroup) => ({
        type: LayerType.LayerGroup,
        rootLayerId: null,
        layerId: "",
        id: layerGroup.id,
        title: layerGroup.title,
        visible: layerGroup.visible,
        expanded: layerGroup.expanded,
        hideInToc: layerGroup.hideInToc,
        layers: layerGroup.layerIds || []
    }));
};
